import { FoodsIcon, HelperBox } from "@foodpilot/foods";
import { Button, Stack, Typography, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import { TrajectoryFormGoal, TrajectoryFormScoreOptions } from "./useTrajectoryForm";
import { NoGoals } from "./Goals/NoGoals";
import { GoalFields } from "./Goals/GoalFields";
import { Fragment } from "react/jsx-runtime";
import { ScoreVariation } from "src/context/EntityBasedScore/ScoreService";

type GoalBlockProps = {
    goals: TrajectoryFormGoal[];
    setGoals: (newGoals: TrajectoryFormGoal[]) => void;
    remainingScoreOptions: TrajectoryFormScoreOptions;
    allScoreOptions: ScoreVariation[];
};
export const GoalBlockList = (props: GoalBlockProps) => {
    const { goals, setGoals, remainingScoreOptions, allScoreOptions } = props;

    const theme = useTheme();
    const { t } = useTranslation();

    const addEmptyBlock = () => {
        const newEmptyGoal: TrajectoryFormGoal = {
            rowId: goals.length,
            score: null,
            variation: null,
            simulationVaryAbsolute: null,
            simulationVaryPercentage: null,
            simulationType: "percentage",
        };
        setGoals([...goals, newEmptyGoal]);
    };

    return (
        <Stack gap={theme.spacing(1)}>
            <Typography variant="h5">{t("actionPlan.trajectory.form.goal.indicator.title")}</Typography>
            {goals.length === 0 ?
                <NoGoals addOneGoal={addEmptyBlock} />
            :   <GoalBlocks
                    goals={goals}
                    setGoals={setGoals}
                    addOneGoal={addEmptyBlock}
                    allScoreOptions={allScoreOptions}
                    remainingScoreOptions={remainingScoreOptions}
                />
            }
        </Stack>
    );
};

type GoalBlocksProps = {
    goals: TrajectoryFormGoal[];
    setGoals: (newGoals: TrajectoryFormGoal[]) => void;
    addOneGoal: () => void;
    remainingScoreOptions: TrajectoryFormScoreOptions;
    allScoreOptions: ScoreVariation[];
};
const GoalBlocks = (props: GoalBlocksProps) => {
    const { goals, setGoals, addOneGoal, remainingScoreOptions, allScoreOptions } = props;

    const theme = useTheme();
    const { t } = useTranslation();

    return (
        <Stack gap={theme.spacing(2)}>
            {goals.map((goal, idx) => {
                return (
                    <Fragment key={`goal-${goal.rowId}-${idx}`}>
                        <GoalFields
                            goal={goal}
                            setGoal={(goal: TrajectoryFormGoal) => {
                                const newGoals = goals.with(goal.rowId, goal);
                                setGoals(newGoals);
                            }}
                            deleteGoal={(goal: TrajectoryFormGoal) => {
                                const newGoals = goals
                                    .filter((g) => g.rowId !== goal.rowId)
                                    .map((g, idx) => {
                                        return { ...g, rowId: idx };
                                    });
                                setGoals(newGoals);
                            }}
                            remainingScoreOptions={remainingScoreOptions}
                            allScoreOptions={allScoreOptions}
                        />
                    </Fragment>
                );
            })}
            <Stack flexDirection={"row"} justifyContent={"start"} paddingLeft={theme.spacing(1)}>
                {remainingScoreOptions.scores.length > 0 ?
                    <Button variant="text" startIcon={<FoodsIcon size={2} icon="add" />} onClick={addOneGoal}>
                        {t("actionPlan.trajectory.form.addGoal")}
                    </Button>
                :   <HelperBox>
                        <Typography variant="body-medium">{t("actionPlan.allIndicators.selected")}</Typography>
                    </HelperBox>
                }
            </Stack>
        </Stack>
    );
};
